import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UsersRound, CalendarClock, Clock, FileBarChart, Settings, Plus, Search,
  Pencil, Trash2, Mail, Phone, Store as StoreIcon, Briefcase, CalendarDays, Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import PersonalFichajes from '../components/personal/PersonalFichajes';
import PersonalCalendario from '../components/personal/PersonalCalendario';
import PersonalInformes from '../components/personal/PersonalInformes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  activo: { label: 'Activo', className: 'bg-green-100 text-green-800 border-green-200' },
  baja: { label: 'Baja', className: 'bg-red-100 text-red-800 border-red-200' },
  inactivo: { label: 'Inactivo', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const COLOR_SWATCHES = ['#3b82f6', '#f97316', '#22c55e', '#ef4444', '#8b5cf6', '#14b8a6', '#f59e0b', '#ec4899', '#0ea5e9', '#64748b'];

const EMPTY_EMPLOYEE = {
  user_id: '', first_name: '', last_name: '', email: '', phone: '',
  store_id: '', position: '', weekly_hours: '', annual_vacation_days: '',
  calendar_color: '#3b82f6', status: 'activo', hire_date: '', weekly_rest_days: [5, 6],
};

const WEEKDAY_CHIPS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const WEEKDAY_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function ComingSoon() { return null; }

export default function Personal() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('empleados');
  const [employees, setEmployees] = useState([]);
  const [stores, setStores] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Employee dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_EMPLOYEE);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Config
  const [config, setConfig] = useState({ default_annual_vacation_days: 30, store_min_staff: {} });
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });

  const storeName = useCallback((id) => stores.find(s => s.id === id)?.name || '—', [stores]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [
        axios.get(`${API}/personal/employees`),
        axios.get(`${API}/stores`),
        axios.get(`${API}/personal/config`),
        axios.get(`${API}/personal/holidays`),
      ];
      if (isAdmin) reqs.push(axios.get(`${API}/personal/available-users`));
      const [empRes, storeRes, cfgRes, holRes, usersRes] = await Promise.all(reqs);
      setEmployees(empRes.data || []);
      setStores(storeRes.data || []);
      setConfig(cfgRes.data || { default_annual_vacation_days: 30, store_min_staff: {} });
      setHolidays(holRes.data || []);
      if (usersRes) setAvailableUsers(usersRes.data || []);
    } catch (e) {
      toast.error('Error al cargar datos de Personal');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.position || '').toLowerCase().includes(q)
    );
  }, [employees, search]);

  const stats = useMemo(() => ({
    total: employees.length,
    activo: employees.filter(e => e.status === 'activo').length,
    baja: employees.filter(e => e.status === 'baja').length,
    inactivo: employees.filter(e => e.status === 'inactivo').length,
  }), [employees]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_EMPLOYEE, annual_vacation_days: config.default_annual_vacation_days });
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      user_id: e.user_id || '', first_name: e.first_name || '', last_name: e.last_name || '',
      email: e.email || '', phone: e.phone || '', store_id: e.store_id || '',
      position: e.position || '', weekly_hours: e.weekly_hours ?? '', annual_vacation_days: e.annual_vacation_days ?? '',
      calendar_color: e.calendar_color || '#3b82f6', status: e.status || 'activo', hire_date: e.hire_date || '',
      weekly_rest_days: Array.isArray(e.weekly_rest_days) ? e.weekly_rest_days : [5, 6],
    });
    setDialogOpen(true);
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    if (!form.first_name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        user_id: form.user_id || null,
        store_id: form.store_id || null,
        weekly_hours: form.weekly_hours === '' ? null : parseFloat(form.weekly_hours),
        annual_vacation_days: form.annual_vacation_days === '' ? null : parseInt(form.annual_vacation_days),
      };
      if (editingId) {
        await axios.put(`${API}/personal/employees/${editingId}`, payload);
        toast.success('Empleado actualizado');
      } else {
        await axios.post(`${API}/personal/employees`, payload);
        toast.success('Empleado creado');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/personal/employees/${deleteId}`);
      toast.success('Empleado eliminado');
      setDeleteId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/personal/config`, {
        default_annual_vacation_days: parseInt(config.default_annual_vacation_days) || 0,
        store_min_staff: Object.fromEntries(
          Object.entries(config.store_min_staff || {}).map(([k, v]) => [k, parseInt(v) || 0])
        ),
      });
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error('Error al guardar configuración');
    }
  };

  const addHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name.trim()) { toast.error('Fecha y nombre obligatorios'); return; }
    try {
      await axios.post(`${API}/personal/holidays`, newHoliday);
      setNewHoliday({ date: '', name: '' });
      const res = await axios.get(`${API}/personal/holidays`);
      setHolidays(res.data || []);
      toast.success('Festivo añadido');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al añadir festivo');
    }
  };

  const removeHoliday = async (id) => {
    try {
      await axios.delete(`${API}/personal/holidays/${id}`);
      setHolidays(prev => prev.filter(h => h.id !== id));
      toast.success('Festivo eliminado');
    } catch (e) {
      toast.error('Error al eliminar festivo');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES') : '—';

  return (
    <div className="space-y-6" data-testid="personal-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center">
          <UsersRound size={22} className="text-orange-400" />
        </div>
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900 leading-none">Personal</h1>
          <p className="text-sm text-slate-500 mt-1">Gestión de empleados, calendario, fichajes e informes</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="empleados" data-testid="tab-empleados"><UsersRound size={16} className="mr-2" />Empleados</TabsTrigger>
          <TabsTrigger value="calendario" data-testid="tab-calendario"><CalendarClock size={16} className="mr-2" />Calendario</TabsTrigger>
          <TabsTrigger value="fichajes" data-testid="tab-fichajes"><Clock size={16} className="mr-2" />Fichajes</TabsTrigger>
          <TabsTrigger value="informes" data-testid="tab-informes"><FileBarChart size={16} className="mr-2" />Informes</TabsTrigger>
          {isAdmin && <TabsTrigger value="config" data-testid="tab-config"><Settings size={16} className="mr-2" />Configuración</TabsTrigger>}
        </TabsList>

        {/* ============ EMPLEADOS ============ */}
        <TabsContent value="empleados" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { k: 'total', label: 'Total', color: 'text-slate-900' },
              { k: 'activo', label: 'Activos', color: 'text-green-600' },
              { k: 'baja', label: 'De baja', color: 'text-red-600' },
              { k: 'inactivo', label: 'Inactivos', color: 'text-slate-500' },
            ].map(s => (
              <Card key={s.k} className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs uppercase font-bold text-slate-400">{s.label}</p>
                  <p className={`text-3xl font-bold ${s.color}`} data-testid={`stat-${s.k}`}>{stats[s.k]}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email o cargo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="employee-search"
              />
            </div>
            {isAdmin && (
              <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="add-employee-btn">
                <Plus size={16} className="mr-2" />Nuevo empleado
              </Button>
            )}
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="spinner w-8 h-8" /></div>
          ) : filteredEmployees.length === 0 ? (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
                <UsersRound size={44} className="mb-3" />
                <p className="font-medium">No hay empleados</p>
                {isAdmin && <p className="text-sm">Pulsa "Nuevo empleado" para crear la primera ficha</p>}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="employees-grid">
              {filteredEmployees.map(e => (
                <Card key={e.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`employee-card-${e.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                        style={{ backgroundColor: e.calendar_color || '#3b82f6' }}>
                        {(e.first_name?.[0] || '') + (e.last_name?.[0] || '')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-bold text-slate-900 truncate">{e.first_name} {e.last_name}</h3>
                          <Badge variant="outline" className={STATUS_META[e.status]?.className}>{STATUS_META[e.status]?.label}</Badge>
                        </div>
                        {e.position && <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><Briefcase size={13} />{e.position}</p>}
                      </div>
                    </div>
                    <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                      {e.email && <p className="flex items-center gap-2 truncate"><Mail size={14} className="text-slate-400" />{e.email}</p>}
                      {e.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-slate-400" />{e.phone}</p>}
                      <p className="flex items-center gap-2"><StoreIcon size={14} className="text-slate-400" />{storeName(e.store_id)}</p>
                      <div className="flex items-center gap-4 pt-1 text-xs text-slate-500">
                        {e.weekly_hours != null && <span>{e.weekly_hours}h/sem</span>}
                        {e.annual_vacation_days != null && <span>{e.annual_vacation_days} días vac.</span>}
                        {e.hire_date && <span className="flex items-center gap-1"><CalendarDays size={12} />{formatDate(e.hire_date)}</span>}
                      </div>
                      {(() => {
                        const rest = Array.isArray(e.weekly_rest_days) ? e.weekly_rest_days : [5, 6];
                        const workDays = 7 - rest.length;
                        const perDay = e.weekly_hours ? Math.round((e.weekly_hours / Math.max(1, workDays)) * 10) / 10 : null;
                        return (
                          <div className="flex items-center gap-2 pt-1.5" data-testid={`employee-schedule-${e.id}`}>
                            <div className="flex gap-1">
                              {WEEKDAY_CHIPS.map((c, i) => (
                                <span key={i} title={`${WEEKDAY_FULL[i]}${rest.includes(i) ? ' (descanso)' : ''}`}
                                  className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${rest.includes(i) ? 'bg-slate-100 text-slate-300' : 'bg-slate-800 text-white'}`}>
                                  {c}
                                </span>
                              ))}
                            </div>
                            <span className="text-xs text-slate-500">{workDays} días{perDay ? ` · ${perDay}h/día` : ''}</span>
                          </div>
                        );
                      })()}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`}>
                          <Pencil size={14} className="mr-1" />Editar
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteId(e.id)} data-testid={`delete-employee-${e.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ============ CALENDARIO ============ */}
        <TabsContent value="calendario" className="mt-4">
          <PersonalCalendario isAdmin={isAdmin} employees={employees} />
        </TabsContent>

        {/* ============ FICHAJES ============ */}
        <TabsContent value="fichajes" className="mt-4">
          <PersonalFichajes isAdmin={isAdmin} employees={employees} />
        </TabsContent>

        {/* ============ INFORMES ============ */}
        <TabsContent value="informes" className="mt-4">
          <PersonalInformes isAdmin={isAdmin} employees={employees} />
        </TabsContent>

        {/* ============ CONFIGURACIÓN ============ */}
        {isAdmin && (
          <TabsContent value="config" className="space-y-4 mt-4">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Vacaciones anuales por defecto</CardTitle></CardHeader>
              <CardContent className="flex items-end gap-3">
                <div className="space-y-1.5">
                  <Label>Días al año</Label>
                  <Input type="number" className="w-32" value={config.default_annual_vacation_days}
                    onChange={(e) => setConfig({ ...config, default_annual_vacation_days: e.target.value })}
                    data-testid="config-vacation-days" />
                </div>
                <p className="text-xs text-slate-400 pb-2.5">Se aplica por defecto al crear nuevos empleados.</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Personal mínimo por tienda</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {stores.length === 0 ? (
                  <p className="text-sm text-slate-400">No hay tiendas configuradas.</p>
                ) : stores.map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-700 flex items-center gap-2"><StoreIcon size={15} className="text-slate-400" />{s.name}</span>
                    <Input type="number" className="w-24" min="0"
                      value={config.store_min_staff?.[s.id] ?? ''}
                      placeholder="0"
                      onChange={(e) => setConfig({ ...config, store_min_staff: { ...config.store_min_staff, [s.id]: e.target.value } })}
                      data-testid={`config-min-staff-${s.id}`} />
                  </div>
                ))}
                <p className="text-xs text-slate-400">No se permitirán solicitudes que dejen la tienda por debajo de este mínimo (Fase Calendario).</p>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={saveConfig} className="bg-orange-500 hover:bg-orange-600" data-testid="save-config-btn">Guardar configuración</Button>
            </div>

            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Festivos</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="space-y-1.5">
                    <Label>Fecha</Label>
                    <Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} data-testid="holiday-date" />
                  </div>
                  <div className="space-y-1.5 flex-1">
                    <Label>Nombre</Label>
                    <Input placeholder="Ej. Navidad" value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} data-testid="holiday-name" />
                  </div>
                  <Button variant="outline" onClick={addHoliday} data-testid="add-holiday-btn"><Plus size={16} className="mr-1" />Añadir</Button>
                </div>
                {holidays.length === 0 ? (
                  <p className="text-sm text-slate-400">No hay festivos configurados.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {holidays.map(h => (
                      <Badge key={h.id} variant="outline" className="pl-2 pr-1 py-1 gap-1.5 text-sm" data-testid={`holiday-${h.id}`}>
                        <span className="font-medium">{formatDate(h.date)}</span>
                        <span className="text-slate-500">{h.name}</span>
                        <button onClick={() => removeHoliday(h.id)} className="ml-1 text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Employee Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar empleado' : 'Nuevo empleado'}</DialogTitle>
            <DialogDescription>Completa la ficha del empleado. Los campos marcados son opcionales salvo el nombre.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nombre *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="form-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Apellidos</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="form-last-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" />
              </div>
              <div className="space-y-1.5">
                <Label>Tienda asignada</Label>
                <Select value={form.store_id || 'none'} onValueChange={(v) => setForm({ ...form, store_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="form-store"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cargo</Label>
                <Input placeholder="Ej. Encargado, Mecánico..." value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="form-position" />
              </div>
              <div className="space-y-1.5">
                <Label>Horas semanales</Label>
                <Input type="number" step="0.5" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })} data-testid="form-weekly-hours" />
              </div>
              <div className="space-y-1.5">
                <Label>Días de vacaciones/año</Label>
                <Input type="number" value={form.annual_vacation_days} onChange={(e) => setForm({ ...form, annual_vacation_days: e.target.value })} data-testid="form-vacation-days" />
              </div>
              <div className="space-y-1.5">
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fecha de incorporación</Label>
                <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="form-hire-date" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Cuenta de usuario enlazada</Label>
                <Select value={form.user_id || 'none'} onValueChange={(v) => setForm({ ...form, user_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="form-user"><SelectValue placeholder="Sin enlazar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin enlazar</SelectItem>
                    {availableUsers.map(u => (
                      <SelectItem key={u.id} value={u.id} disabled={u.linked && u.id !== form.user_id}>
                        {u.name} ({u.email}){u.linked && u.id !== form.user_id ? ' — ya enlazado' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Días libres semanales</Label>
              <div className="flex gap-2" data-testid="form-rest-days">
                {WEEKDAY_CHIPS.map((c, i) => {
                  const active = form.weekly_rest_days.includes(i);
                  return (
                    <button key={i} type="button" title={WEEKDAY_FULL[i]}
                      onClick={() => setForm({
                        ...form,
                        weekly_rest_days: active
                          ? form.weekly_rest_days.filter(d => d !== i)
                          : [...form.weekly_rest_days, i].sort((a, b) => a - b),
                      })}
                      className={`w-9 h-9 rounded-full text-sm font-bold transition-colors ${active ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      data-testid={`rest-day-${i}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400">Días de descanso habitual. No se descuentan de las vacaciones.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-2"><Palette size={14} />Color en el calendario</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_SWATCHES.map(c => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, calendar_color: c })}
                    className={`w-8 h-8 rounded-full transition-transform ${form.calendar_color === c ? 'ring-2 ring-offset-2 ring-slate-900 scale-110' : ''}`}
                    style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-employee-btn">
                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear empleado')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empleado?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-employee">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
